import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { createTheme, alpha, Theme } from '@mui/material/styles';
import {
  SchoolSettings,
  getSchoolSettings,
  updateSchoolSettings as updateMockSettings,
  defaultSchoolSettings,
} from '../mock/schoolSettings';

interface SchoolSettingsContextType {
  settings: SchoolSettings;
  theme: Theme;
  updateSettings: (updates: Partial<Omit<SchoolSettings, 'id'>>) => void;
  resetSettings: () => void;
}

const SchoolSettingsContext = createContext<SchoolSettingsContextType | null>(null);

const createAppTheme = (primaryColor: string, secondaryColor: string): Theme => {
  return createTheme({
    palette: {
      primary: {
        main: primaryColor,
        light: secondaryColor,
        dark: primaryColor,
        contrastText: '#fff',
      },
      secondary: {
        main: '#16a34a',
        light: '#4ade80',
        dark: '#15803d',
        contrastText: '#fff',
      },
      error: {
        main: '#dc2626',
        light: '#ef4444',
        dark: '#991b1b',
      },
      warning: {
        main: '#ea580c',
        light: '#fb923c',
        dark: '#c2410c',
      },
      info: {
        main: '#0284c7',
        light: '#38bdf8',
        dark: '#075985',
      },
      success: {
        main: '#16a34a',
        light: '#4ade80',
        dark: '#15803d',
      },
      background: {
        default: '#f3f4f6',
        paper: '#ffffff',
      },
      grey: {
        50: '#f9fafb',
        100: '#f3f4f6',
        200: '#e5e7eb',
        300: '#d1d5db',
        400: '#9ca3af',
        500: '#6b7280',
        600: '#4b5563',
        700: '#374151',
        800: '#1f2937',
        900: '#111827',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '3rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '2.25rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.875rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      subtitle1: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      '0px 2px 4px rgba(0,0,0,0.05)',
      '0px 4px 8px rgba(0,0,0,0.08)',
      '0px 8px 16px rgba(0,0,0,0.1)',
      '0px 12px 24px rgba(0,0,0,0.12)',
      '0px 16px 32px rgba(0,0,0,0.14)',
      '0px 20px 40px rgba(0,0,0,0.16)',
      '0px 24px 48px rgba(0,0,0,0.18)',
      '0px 2px 4px rgba(0,0,0,0.05)',
      '0px 4px 8px rgba(0,0,0,0.08)',
      '0px 8px 16px rgba(0,0,0,0.1)',
      '0px 12px 24px rgba(0,0,0,0.12)',
      '0px 16px 32px rgba(0,0,0,0.14)',
      '0px 20px 40px rgba(0,0,0,0.16)',
      '0px 24px 48px rgba(0,0,0,0.18)',
      '0px 2px 4px rgba(0,0,0,0.05)',
      '0px 4px 8px rgba(0,0,0,0.08)',
      '0px 8px 16px rgba(0,0,0,0.1)',
      '0px 12px 24px rgba(0,0,0,0.12)',
      '0px 16px 32px rgba(0,0,0,0.14)',
      '0px 20px 40px rgba(0,0,0,0.16)',
      '0px 24px 48px rgba(0,0,0,0.18)',
      '0px 2px 4px rgba(0,0,0,0.05)',
      '0px 4px 8px rgba(0,0,0,0.08)',
      '0px 8px 16px rgba(0,0,0,0.1)',
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 24px',
            borderRadius: '10px',
            transition: 'all 0.2s ease-in-out',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 8px 16px rgba(0,0,0,0.15)',
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(0px)',
            },
          },
          outlined: {
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px',
              backgroundColor: alpha(primaryColor, 0.04),
            },
          },
          sizeLarge: {
            padding: '14px 32px',
            fontSize: '1rem',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 1px 2px rgba(0,0,0,0.1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: '16px',
            '&:hover': {
              boxShadow: '0px 10px 25px rgba(0,0,0,0.1), 0px 4px 10px rgba(0,0,0,0.05)',
              transform: 'translateY(-4px)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
          },
          elevation1: {
            boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 1px 2px rgba(0,0,0,0.1)',
          },
          elevation2: {
            boxShadow: '0px 4px 6px rgba(0,0,0,0.05), 0px 2px 4px rgba(0,0,0,0.1)',
          },
          elevation3: {
            boxShadow: '0px 10px 15px rgba(0,0,0,0.05), 0px 4px 6px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              transition: 'all 0.2s ease-in-out',
              '&:hover fieldset': {
                borderColor: alpha(primaryColor, 0.5),
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
          },
          standardInfo: {
            backgroundColor: alpha('#0284c7', 0.1),
            color: '#075985',
          },
          standardSuccess: {
            backgroundColor: alpha('#16a34a', 0.1),
            color: '#15803d',
          },
          standardWarning: {
            backgroundColor: alpha('#ea580c', 0.1),
            color: '#c2410c',
          },
          standardError: {
            backgroundColor: alpha('#dc2626', 0.1),
            color: '#991b1b',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 1px 2px rgba(0,0,0,0.1)',
          },
        },
      },
    },
  });
};

export const SchoolSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SchoolSettings>(getSchoolSettings);

  const theme = useMemo(
    () => createAppTheme(settings.gradientStartColor, settings.gradientEndColor),
    [settings.gradientStartColor, settings.gradientEndColor]
  );

  const updateSettings = useCallback((updates: Partial<Omit<SchoolSettings, 'id'>>) => {
    const updated = updateMockSettings(updates);
    setSettings(updated);
  }, []);

  const resetSettings = useCallback(() => {
    const updated = updateMockSettings({
      schoolName: defaultSchoolSettings.schoolName,
      logoUrl: defaultSchoolSettings.logoUrl,
      gradientStartColor: defaultSchoolSettings.gradientStartColor,
      gradientEndColor: defaultSchoolSettings.gradientEndColor,
    });
    setSettings(updated);
  }, []);

  const value = useMemo(
    () => ({ settings, theme, updateSettings, resetSettings }),
    [settings, theme, updateSettings, resetSettings]
  );

  return (
    <SchoolSettingsContext.Provider value={value}>
      {children}
    </SchoolSettingsContext.Provider>
  );
};

export const useSchoolSettings = (): SchoolSettingsContextType => {
  const context = useContext(SchoolSettingsContext);
  if (!context) {
    throw new Error('useSchoolSettings must be used within a SchoolSettingsProvider');
  }
  return context;
};
